import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Link as MuiLink, Stack, Typography } from "@mui/material";
import { ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

export interface PageHeaderProps {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  /** Route of the parent page; renders a back link above the title. */
  backTo?: string;
  backLabel?: string;
  eyebrow?: string;
  /**
   * Design: page titles are uppercase expanded Archivo. Pass `false` for titles that are
   * sentences or names (the Today greeting, a student's name).
   */
  uppercase?: boolean;
  /** Extra content under the title column (e.g. tally pills). */
  children?: ReactNode;
}

/** The page's single `<h1>` (36/900 expanded uppercase), an optional muted sub-line and right-aligned actions. */
export const PageHeader = ({
  title,
  description,
  actions,
  backTo,
  backLabel,
  eyebrow,
  uppercase = true,
  children,
}: PageHeaderProps) => {
  const { t } = useTranslation();
  const appName = t("app.name");

  useEffect(() => {
    document.title = `${title} · ${appName}`;
  }, [title, appName]);

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      alignItems={{ xs: "flex-start", md: "flex-end" }}
      justifyContent="space-between"
      spacing={2.5}
      sx={{ mb: 2.75 }}
    >
      <Box
        sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}
      >
        {backTo && (
          <MuiLink
            component={RouterLink}
            to={backTo}
            underline="hover"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              fontSize: 13,
              fontWeight: 700,
              color: "primaryInk",
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 16 }} aria-hidden="true" />
            {backLabel ?? t("common.back", { defaultValue: "Back" })}
          </MuiLink>
        )}
        {eyebrow && (
          <Typography
            variant="overline"
            component="p"
            sx={{ color: "dangerInk", letterSpacing: ".12em", lineHeight: 1 }}
          >
            {eyebrow}
          </Typography>
        )}
        <Typography
          variant="h1"
          component="h1"
          sx={{
            color: "text.primary",
            textTransform: uppercase ? "uppercase" : "none",
            letterSpacing: uppercase ? "-.01em" : "-.015em",
            fontSize: { xs: 30, sm: 36 },
          }}
        >
          {title}
        </Typography>
        {description && (
          <Typography
            component="p"
            sx={{ fontSize: 15, lineHeight: 1.4, color: "text.secondary" }}
          >
            {description}
          </Typography>
        )}
        {children}
      </Box>
      {actions && (
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ flexShrink: 0 }}
        >
          {actions}
        </Stack>
      )}
    </Stack>
  );
};

export default PageHeader;
